// ** React Imports
import { useState, useCallback, useContext } from "react";
import { useHistory } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { openAuthModal } from "store/modules/global-common.slice";

// ** Third Party Components
import { toast } from "helpers/toastWrapper";
import { useTranslation } from "react-i18next";

// ** Custom Components
import Block from "components/layout/Block";
import { Condition } from "components/conditional";
import { ActionButton } from "components/layout/ActionButton";

// ** Utils & Helpers
import api from "helpers/ApiClient";
import pseudoFormat from "utils/pseudoFormat";

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const InteractionsInMediaPostProfile = ({
	type,
	userId,
	mediaId,
	canLike,
	isMe,
	onCommentClick,
	setIsRemoved,
	iLiked,
	setILiked,
	likesCount,
	setLikesCount,
	iCommented,
	commentsCount,
	isActiveComment,
}) => {
	const dispatch = useDispatch();

	const { isPhone, isTablet } = useContext(deviceTypeContext);
	const suspension = useSelector((state) => state.globalUser?.suspension);
	const isBan = suspension?.duration > 0;
	const { t } = useTranslation(["common", "suspensions", "pages"]);

	const history = useHistory();
	const [disabledLike, setDisabledLike] = useState(false);
	const [isBeingRemoved, setIsBeingRemoved] = useState(false);

	const onLikeClick = useCallback(() => {
		setDisabledLike(true);
		if (iLiked) {
			api.delete(`/like?user=${userId}&id=${mediaId}&type=${type}`, {
				noTimeout: true,
			})
				.then(() => {
					setLikesCount(likesCount > 0 ? likesCount - 1 : 0);
					setILiked(false);
				})
				.catch((e) => {
					if (e?.response?.data?.code === 99) {
						setLikesCount(likesCount > 0 ? likesCount - 1 : 0);
						setILiked(false);
					}
					toast.error(e.response.data.error);
				})
				.finally(() => {
					setDisabledLike(false);
				});
		} else {
			api.put(
				`/like?user=${userId}&id=${mediaId}&type=${type}`,
				{},
				{ noTimeout: true }
			)
				.then(() => {
					setILiked(true);
					setLikesCount(likesCount + 1);
				})
				.catch((e) => {
					if (e?.response?.data?.code === 99) {
						setILiked(true);
						setLikesCount(likesCount + 1);
					}
					toast.error(e.response.data.error);
				})
				.finally(() => {
					setDisabledLike(false);
				});
		}
	}, [iLiked, userId, mediaId]);

	const isAuth = useSelector((state) => state.globalCommon.isAuth);

	const tooltipLikeText = () => {
		switch (type) {
			case "photo":
				return "canNotLikePhoto";
			case "video":
				return "canNotLikeVideo";
			case "post":
				return "canNotLikePost";
			default:
				return "";
		}
	};

	return (
		<Block flex>
			<ActionButton
				isHeart
				onClick={(event) => {
					if (isAuth) {
						onLikeClick(event);
					} else {
						dispatch(openAuthModal());
					}
				}}
				disabled={!canLike || disabledLike || isBan}
				noColorDisabled={disabledLike}
				liked={iLiked}
				label={likesCount > 0 ? pseudoFormat(likesCount) : null}
				customSvgSize={"21px"}
				customMargin={"0 0 0 0"}
				tooltipInfo={
					isBan
						? t("suspensions:bannedAccount")
						: canLike
						? false
						: t(tooltipLikeText())
				}
				tooltipPlacement={{
					anchor: "RIGHT_CENTER",
				}}
			/>
			<ActionButton
				isComment
				isActive={isActiveComment}
				noHover={isPhone || isTablet}
				onClick={(event) => {
					if (onCommentClick) {
						onCommentClick({
							mediaId,
							type,
							originalEvent: event,
						});
					}
				}}
				commented={iCommented}
				label={commentsCount > 0 ? pseudoFormat(commentsCount) : null}
				customSvgSize={"21px"}
			/>
			<Condition value={isMe}>
				<ActionButton
					isPen
					disabled={isBan}
					tooltipInfo={isBan ? t("suspensions:bannedAccount") : ""}
					onClick={() => {
						switch (type) {
							case "post":
								history.push(
									`/userpanel/gallery/posts/edit/${mediaId}`
								);
								break;
							case "photo":
								history.push(
									`/userpanel/gallery/photos/browse/edit/${mediaId}`
								);
								break;
							case "video":
								history.push(
									`/userpanel/gallery/videos/browse/edit/${mediaId}`
								);
								break;
							default:
								break;
						}
					}}
					customSvgSize={"19px"}
				/>
				<ActionButton
					isTrash
					onClick={() => {
						setIsBeingRemoved(true);
						api.delete(`/${type}?id=${mediaId}`)
							.then(() => {
								setIsBeingRemoved(false);
								setIsRemoved(true);
							})
							.catch((error) => {
								setIsBeingRemoved(false);
								if (error?.response?.data?.error) {
									toast.error(`${error.response.data.error}`);
								}
							});
					}}
					disabled={isBeingRemoved}
					customSvgSize={"19px"}
					tooltipInfo={""}
				/>
			</Condition>
		</Block>
	);
};

// #####################################################

export default InteractionsInMediaPostProfile;
